import React, { useEffect, useState } from 'react';
import { PhoneIcon, PhoneOff, Video, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSocketEmitter } from '@/hooks/use-sockets';
import { useContext } from 'react';
import { AuthContext } from '@/context/Auth';
import { client } from '@/api';
import toast from 'react-hot-toast';

/**
 * Clean instant call notification component
 * Handles incoming call requests and navigation
 */
const InstantCallNotification = ({ 
  userName, 
  mode, 
  userPicture, 
  userSocketId,
  userId,
  channelName, 
  onDismiss 
}) => {
  const navigate = useNavigate();
  const { emitter } = useSocketEmitter();
  const { astrologer } = useContext(AuthContext);
  const [isProcessing, setIsProcessing] = useState(false);

  console.log('[InstantCallNotification] Props:', { 
    userName, mode, userPicture, userSocketId, userId, channelName 
  });

  // Validate required props
  if (!userName || !mode || !userSocketId || !channelName) {
    console.error('[InstantCallNotification] Missing required props:', {
      userName: !!userName,
      mode: !!mode, 
      userSocketId: !!userSocketId,
      userId: !!userId,
      channelName: !!channelName
    });
    return null;
  }

  // Auto-dismiss timer
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isProcessing) {
        handleTimeout();
      }
    }, 30000); // 30 seconds timeout

    return () => clearTimeout(timer);
  }, [isProcessing]);

  // Pre-warm media permission as soon as the notification appears.
  // Most browsers (Safari especially) require a prior grant before the
  // accept tap can synchronously open the mic; doing it here — while
  // the astrologer is reading the incoming-call card — means the accept
  // path becomes a no-op for permission. Fails silently; the real
  // request inside handleAccept will surface the error if the user
  // denies.
  useEffect(() => {
    const constraints = mode === 'video'
      ? { audio: {}, video: {} }
      : { audio: {}, video: false };
    navigator.mediaDevices
      ?.getUserMedia(constraints)
      .then((stream) => {
        console.log('[InstantCallNotification] Pre-warmed media permission');
        stream.getTracks().forEach((t) => t.stop());
      })
      .catch((err) => {
        console.log('[InstantCallNotification] Pre-warm failed (ok):', err?.name);
      });
  }, [mode]);

  // Play notification sound
  useEffect(() => {
    try {
      const audio = new Audio('/notification.mp3');
      audio.play().catch(e => 
        console.log('[InstantCallNotification] Audio play failed:', e.message)
      );
    } catch (error) {
      console.log('[InstantCallNotification] Audio setup failed:', error.message);
    }
  }, []);

  const handleTimeout = async () => {
    console.log('[InstantCallNotification] Call timed out');
    
    // Update call status in database
    try {
      await client.patch(`/api/calls/channel/${channelName}/status`, {
        status: 'timeout',
        endReason: 'timeout'
      });
    } catch (error) {
      console.error('[InstantCallNotification] Failed to update call status:', error);
    }
    
    emitter('instant-call-response', {
      userSocketId,
      status: false,
      message: 'Call request timed out'
    });
    
    toast.error('Call request timed out');
    onDismiss?.();
  };

  const handleAccept = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    console.log('[InstantCallNotification] Accepting call');
    
    try {
      // Request media permissions. Use the explicit-object form rather
      // than the boolean shorthand because Safari has been observed to
      // return OverconstrainedError on `{audio: true, video: false}`
      // in some versions, especially when permissions haven't been
      // pre-granted. The empty constraint object `{}` is equivalent to
      // `true` but is what Safari/WebKit actually expects.
      const constraints = mode === 'video'
        ? { audio: {}, video: {} }
        : { audio: {}, video: false };

      console.log('[InstantCallNotification] Requesting getUserMedia with:', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('[InstantCallNotification] Media permissions granted, tracks:',
        stream.getTracks().map(t => `${t.kind}:${t.label}`).join(', '));
      // Release immediately — Agora SDK will re-acquire on the call page.
      // Holding the stream here just causes the mic indicator to flicker
      // and can keep the device busy while we navigate.
      stream.getTracks().forEach(t => t.stop());
      
      // Update call status in database
      try {
        await client.patch(`/api/calls/channel/${channelName}/status`, {
          status: 'accepted'
        });
        console.log('[InstantCallNotification] Call status updated to accepted');
      } catch (error) {
        console.error('[InstantCallNotification] Failed to update call status:', error);
        // Continue anyway - socket event is the primary mechanism
      }
      
      // Send acceptance response via socket
      emitter('instant-call-response', {
        userSocketId,
        status: true,
        channelName,
        mode,
        astrologerName: astrologer?.personalInformation?.name || 'Astrologer'
      });
      
      // Navigate to unified call page
      const callRoute = `/calls/${encodeURIComponent(channelName)}`;
      console.log('[InstantCallNotification] Navigating to:', callRoute);
      
      navigate(callRoute, {
        state: { 
          channel: channelName, 
          userSocketId,
          userId,
          userName,
          userPicture,
          mode
        }
      });
      
      onDismiss?.();
      
    } catch (error) {
      console.error('[InstantCallNotification] Accept failed:', error);
      setIsProcessing(false);
      
      // Map common getUserMedia errors to actionable user messages.
      // - NotAllowedError    → user explicitly denied permission
      // - NotFoundError      → no mic/camera attached at OS level
      // - NotReadableError   → mic/camera in use by another app
      // - OverconstrainedError → no device satisfies the constraint
      //                          (often means no mic at all on Safari)
      // - SecurityError      → page isn't on https
      switch (error?.name) {
        case 'NotAllowedError':
          toast.error('Microphone access was denied. Open browser settings to enable it for this site.');
          break;
        case 'NotFoundError':
          toast.error('No microphone detected. Please connect a microphone and try again.');
          break;
        case 'NotReadableError':
          toast.error('Your microphone is being used by another app. Close it and try again.');
          break;
        case 'OverconstrainedError':
          toast.error('Microphone/camera not available. Check that your device has a working mic and browser permission is granted.');
          break;
        case 'SecurityError':
          toast.error('Calls require a secure (HTTPS) connection.');
          break;
        default:
          toast.error('Failed to start call. Please try again.');
      }
    }
  };

  const handleReject = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    console.log('[InstantCallNotification] Rejecting call');
    
    // Update call status in database
    try {
      await client.patch(`/api/calls/channel/${channelName}/status`, {
        status: 'rejected',
        endReason: 'astrologer_ended'
      });
      console.log('[InstantCallNotification] Call status updated to rejected');
    } catch (error) {
      console.error('[InstantCallNotification] Failed to update call status:', error);
    }
    
    // Send rejection response via socket
    emitter('instant-call-response', {
      userSocketId,
      status: false,
      message: 'Call rejected by astrologer'
    });
    
    toast('Call rejected');
    onDismiss?.();
  };

  const CallIcon = mode === 'video' ? Video : Phone;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 animate-scale-in">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center">
            <CallIcon className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">
            Incoming {mode === 'video' ? 'Video' : 'Voice'} Call
          </h3>
        </div>

        {/* User Info */}
        <div className="flex items-center mb-6">
          <img
            src={userPicture || '/default-avatar.png'}
            alt={userName}
            className="w-12 h-12 rounded-full border-2 border-orange-200 mr-4"
            onError={(e) => {
              e.target.src = '/default-avatar.png';
            }}
          />
          <div className="flex-1">
            <p className="font-semibold text-gray-800 text-lg">{userName}</p>
            <p className="text-sm text-gray-500">
              Requesting {mode === 'video' ? 'video' : 'voice'} consultation
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={handleReject}
            disabled={isProcessing}
            className="flex-1 flex items-center justify-center py-3 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-semibold rounded-xl transition-colors shadow-lg"
          >
            <PhoneOff className="w-5 h-5 mr-2" />
            Decline
          </button>
          
          <button
            onClick={handleAccept}
            disabled={isProcessing}
            className="flex-1 flex items-center justify-center py-3 bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white font-semibold rounded-xl transition-colors shadow-lg"
          >
            <PhoneIcon className="w-5 h-5 mr-2" />
            Accept
          </button>
        </div>

        {isProcessing && (
          <div className="mt-4 text-center">
            <div className="inline-flex items-center text-sm text-gray-500">
              <div className="animate-spin w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full mr-2"></div>
              Processing...
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstantCallNotification;
